/**
 * Obstruction Form Wizard
 *
 * 4-section tabbed form:
 * 1. Basic info — title, type, severity, location (GPS), description + schedule
 *    (planned dates, traffic impact)
 * 2. Contractor — 9 contractor fields
 * 3. Affected roads — inline list of additional affected roads/sections
 * 4. Attachments — file uploads (optional)
 *
 * The map anchor point is placed before the form opens, so there is no
 * geometry section here.
 *
 * Mirrors `TaskFormWizard`: a thin container wiring `SlidePanel`,
 * `DetailPanelLayout` (section tabs ↔ sidebar) and the section renderer via the
 * orchestrator hook (`useObstructionFormWizardLogic`). Free navigation; the
 * footer's submit validates the whole form and incomplete sections show a dot.
 */
import { useMemo, type ReactElement } from 'react';
import { Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { SlidePanel } from '@/components/ui/SlidePanel';
import {
  useWizardSectionNav,
  WizardStepRenderer,
} from '@/components/common/wizard';
import { DetailPanelLayout } from '@/components/common/DetailPanelLayout';
import { FormFooter, FormPanelTitle } from '@/components/common/form';
import { useSectionLayoutMode } from '@/hooks';
import { useSlidePanelVariant } from '@/hooks/useSlidePanelVariant';
import { useGeoMarkupStore } from '@/store';
import { selectDetailedObstruction } from '@/store/selectors/obstructionSelectors';
import type { ObstructionWizardFormState } from '../types';

import { OBSTRUCTION_STEP_DESCRIPTORS } from '../config/obstructionStepDescriptors';
import { useObstructionFormWizardLogic } from '../hooks/useObstructionFormWizardLogic';

import styles from '@/components/common/wizard/wizardContent.module.css';

export function ObstructionFormWizard(): ReactElement | null {
  const isOpen = useGeoMarkupStore((state) => state.obstructions.formIsOpen);
  const formMode = useGeoMarkupStore((state) => state.obstructions.formMode);
  const detailedObstruction = useGeoMarkupStore(selectDetailedObstruction);
  const formEditingId = useGeoMarkupStore(
    (state) => state.obstructions.formEditingId
  );
  const setMobilePanelMinimized = useGeoMarkupStore(
    (state) => state.ui.setMobilePanelMinimized
  );

  const editingObstruction = useMemo(() => {
    if (formMode !== 'edit' || !formEditingId) return null;
    // `formEditingId` may still be the shortId while the fetched detail carries
    // the GUID (and vice-versa after the shortId→GUID upgrade) — match on either
    // so the form seeds as soon as the detail for this obstruction is available.
    if (
      detailedObstruction &&
      (detailedObstruction.id === formEditingId ||
        detailedObstruction.shortId === formEditingId)
    ) {
      return detailedObstruction;
    }
    return null;
  }, [formMode, formEditingId, detailedObstruction]);

  const { t } = useTranslation();
  const slidePanelVariant = useSlidePanelVariant();
  const { isSidebar } = useSectionLayoutMode();

  const {
    form,
    handleClose,
    handleStepClick,
    isSubmitting,
    handleSubmit,
    handleDeleteExistingAttachment,
    anchorPoint,
  } = useObstructionFormWizardLogic({ editingObstruction });

  // Existing (already-persisted) attachments surfaced in the Attachments step
  // when editing. The domain shape already matches `ExistingAttachmentItem`.
  const existingAttachments = useMemo(
    () => editingObstruction?.attachments ?? [],
    [editingObstruction]
  );

  // Single source of truth for the wizard sections (fixed 4 — module constant).
  const stepDescriptors = OBSTRUCTION_STEP_DESCRIPTORS;

  // Per-section validity (anchor lives in Zustand, so merge it into the values
  // the geometry section validates against). Drives both the dots and submit.
  const sectionValidity = useMemo(
    () =>
      stepDescriptors.map(
        (descriptor) =>
          descriptor.validate({
            values: { ...form.values, anchorPoint },
            formErrors: form.errors as Partial<
              Record<keyof ObstructionWizardFormState, string>
            >,
            t,
          }).isValid
      ),
    [stepDescriptors, form.values, form.errors, anchorPoint, t]
  );

  const canSave = useMemo(
    () => sectionValidity.every(Boolean),
    [sectionValidity]
  );

  // Bridge the numeric section pointer (`currentStep`) to DetailPanelLayout's
  // string-keyed section API; free navigation + submit-time validation.
  const { activeSection, handleSectionChange, sectionItems } =
    useWizardSectionNav({
      stepDescriptors,
      currentStep: form.values.currentStep,
      sectionValidity,
      onStepSelect: handleStepClick,
    });

  const headerContent = useMemo(
    () => (
      <FormPanelTitle>
        {editingObstruction
          ? t('obstructions.wizard.editTitle')
          : t('obstructions.wizard.createTitle')}
      </FormPanelTitle>
    ),
    [editingObstruction, t]
  );

  const footerContent = useMemo(
    () => (
      <FormFooter
        leftActions={
          <Button
            variant="subtle"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
        }
        rightActions={
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSave}
          >
            {editingObstruction
              ? t('obstructions.wizard.actions.update')
              : t('obstructions.wizard.actions.create')}
          </Button>
        }
      />
    ),
    [handleClose, isSubmitting, t, handleSubmit, canSave, editingObstruction]
  );

  const collapsedActions = useMemo(
    () => ({
      onCancel: handleClose,
      onSave: handleSubmit,
      onExpand: () => setMobilePanelMinimized(false),
      canSave,
      isSubmitting,
      saveLabel: editingObstruction
        ? t('obstructions.wizard.actions.update')
        : t('obstructions.wizard.actions.create'),
    }),
    [
      handleClose,
      handleSubmit,
      setMobilePanelMinimized,
      canSave,
      isSubmitting,
      editingObstruction,
      t,
    ]
  );

  return (
    <SlidePanel
      opened={isOpen}
      onClose={handleClose}
      variant={slidePanelVariant}
      headerTitle={headerContent}
      footerContent={footerContent}
      width={480}
      withCloseButton={true}
      closeButtonAriaLabel={t('obstructions.wizard.actions.close')}
      collapsedActions={collapsedActions}
      fullscreenWideContent={isSidebar}
      defaultFullscreen={false}
    >
      <DetailPanelLayout
        sectionItems={sectionItems}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        contentVariant="flex"
        contentClassName={
          isSidebar ? styles.wizardContentSidebar : styles.wizardContentTabs
        }
      >
        <WizardStepRenderer
          steps={stepDescriptors}
          currentStep={form.values.currentStep}
          renderProps={{
            form,
            isSubmitting,
            existingAttachments,
            onDeleteExistingAttachment: editingObstruction
              ? handleDeleteExistingAttachment
              : undefined,
          }}
        />
      </DetailPanelLayout>
    </SlidePanel>
  );
}
